import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * This custom hook enables scrolling to form field errors.
 * It is useful in situations where the ref for a custom field
 * is not available in react-hook-form.
 * This can occur, for example, when dealing with radio groups where
 * the radio inputs share a common name or hidden inputs
 * with inaccessible refs due to their hidden nature.
 */
export const useScrollToFormFieldError = <TElement extends HTMLElement>(
  field: string
) => {
  const {
    formState: { errors, isSubmitting },
  } = useFormContext();
  const ref = useRef<TElement | null>(null);

  useEffect(() => {
    const [firstErrorField] = Object.keys(errors);
    if (firstErrorField === field && !isSubmitting) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'end',
      });
      ref.current?.focus({ preventScroll: true });
    }
  }, [errors, isSubmitting, field]);

  return {
    ref,
  };
};
